import { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Tooltip,
  Popover,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  Search as SearchIcon,
  Add as AddIcon,
  ManageAccounts as ManageAccountsIcon,
} from '@mui/icons-material';
import { customerService } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

const UserManagement = () => {
  const { t } = useTranslation();
  const [currentTab, setCurrentTab] = useState(0);
  const [collaborators, setCollaborators] = useState([]);
  const [businesses, setBusinesses] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [selectedCollaborator, setSelectedCollaborator] = useState(null);
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    role_uuid: '',
    selectedBusinesses: [],
    allBusinesses: false,
  });
  const [inviteForm, setInviteForm] = useState({
    name: '',
    email: '',
    role_uuid: '',
    allBusinesses: true,
    selectedBusinesses: [],
  });
  const [unitsPopover, setUnitsPopover] = useState({
    anchorEl: null,
    businesses: [],
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [collaboratorsRes, businessesRes, rolesRes] = await Promise.all([
        customerService.getCollaborators(),
        customerService.getCustomerBusinesses(),
        customerService.getCustomerRoles(),
      ]);
      setCollaborators(collaboratorsRes.data.results);
      setBusinesses(businessesRes.data.results);
      setRoles(rolesRes.data);
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleInviteUser = () => {
    setInviteForm({
      name: '',
      email: '',
      role_uuid: '',
      allBusinesses: true,
      selectedBusinesses: [],
    });
    setInviteModalOpen(true);
  };

  const handleCloseInviteModal = () => {
    setInviteModalOpen(false);
  };

  const handleInvite = async () => {
    try {
      if (!inviteForm.name || !inviteForm.email || !inviteForm.role_uuid) {
        setSnackbar({
          open: true,
          message: t('userManagement.fillAllFieldsError'),
          severity: 'error',
        });
        return;
      }

      if (!inviteForm.allBusinesses && inviteForm.selectedBusinesses.length === 0) {
        setSnackbar({
          open: true,
          message: t('userManagement.selectAtLeastOneUnitError'),
          severity: 'error',
        });
        return;
      }

      const businessScope = inviteForm.allBusinesses ? 'all' : 'limited';

      const payload = {
        name: inviteForm.name,
        email: inviteForm.email,
        business_scope: businessScope,
      };

      // Backend espera 'global_role' quando scope é 'all', e 'role' quando é 'limited'
      if (businessScope === 'all') {
        payload.global_role = inviteForm.role_uuid;
      } else {
        payload.role = inviteForm.role_uuid;
        payload.businesses = inviteForm.selectedBusinesses;
      }

      await customerService.inviteCollaborator(payload);

      setSnackbar({
        open: true,
        message: t('userManagement.inviteSuccess'),
        severity: 'success',
      });

      setInviteModalOpen(false);

      fetchInitialData();
    } catch (err) {
      console.error('Error inviting collaborator:', err);
      setSnackbar({
        open: true,
        message: err.response?.data?.detail || t('userManagement.inviteError'),
        severity: 'error',
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleInviteFormChange = (field, value) => {
    setInviteForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleInviteAllBusinessesToggle = (checked) => {
    setInviteForm(prev => ({
      ...prev,
      allBusinesses: checked,
      selectedBusinesses: checked ? [] : prev.selectedBusinesses,
    }));
  };

  const handleInviteBusinessToggle = (businessUuid) => {
    setInviteForm(prev => {
      const newSelected = prev.selectedBusinesses.includes(businessUuid)
        ? prev.selectedBusinesses.filter(id => id !== businessUuid)
        : [...prev.selectedBusinesses, businessUuid];

      return {
        ...prev,
        selectedBusinesses: newSelected,
      };
    });
  };

  const handleEdit = (collaborator) => {
    setSelectedCollaborator(collaborator);

    // Determine if all businesses are selected
    const allBusinesses = collaborator.business_scope === 'all';

    setEditForm({
      name: collaborator.name,
      email: collaborator.email,
      role_uuid: collaborator.role?.uuid || '',
      selectedBusinesses: allBusinesses ? [] : (collaborator.businesses || []).map(b => b.uuid),
      allBusinesses: allBusinesses,
    });

    setEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setEditModalOpen(false);
    setSelectedCollaborator(null);
  };

  const handleSaveEdit = async () => {
    console.log('Save edit:', editForm);
    // TODO: Implement API call to update collaborator
    setEditModalOpen(false);
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleAllBusinessesToggle = (checked) => {
    setEditForm(prev => ({
      ...prev,
      allBusinesses: checked,
      selectedBusinesses: checked ? [] : prev.selectedBusinesses,
    }));
  };

  const handleBusinessToggle = (businessUuid) => {
    setEditForm(prev => {
      const newSelected = prev.selectedBusinesses.includes(businessUuid)
        ? prev.selectedBusinesses.filter(id => id !== businessUuid)
        : [...prev.selectedBusinesses, businessUuid];

      return {
        ...prev,
        selectedBusinesses: newSelected,
      };
    });
  };

  const handleToggleStatus = (id, currentStatus) => {
    console.log('Toggle status for:', id, currentStatus);
    // TODO: Implement status toggle
  };

  const getRoleLabel = (collaborator) => {
    return collaborator.role || 'N/A';
  };

  const handleOpenUnitsPopover = (event, businesses) => {
    setUnitsPopover({
      anchorEl: event.currentTarget,
      businesses: businesses,
    });
  };

  const handleCloseUnitsPopover = () => {
    setUnitsPopover({
      anchorEl: null,
      businesses: [],
    });
  };

  const renderBusinesses = (collaborator) => {
    // business_scope === 'all' - mostra "Todas as unidades" traduzido
    if (collaborator.business_scope === 'all') {
      return (
        <Typography variant="body2" color="text.secondary">
          {t('userManagement.allUnits')}
        </Typography>
      );
    }

    // business_scope === 'limited' - renderiza as unidades específicas
    const businessesList = collaborator.businesses || [];

    if (businessesList.length === 0) {
      return (
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      );
    }

    // Uma única unidade
    if (businessesList.length === 1) {
      return (
        <Typography variant="body2" color="text.secondary">
          {businessesList[0].name}
        </Typography>
      );
    }

    // Múltiplas unidades - primeira + chip com contador
    const firstBusiness = businessesList[0];
    const remainingCount = businessesList.length - 1;
    const allBusinessNames = businessesList.map(b => b.name).join('\n');

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="body2" color="text.secondary">
          {firstBusiness.name}
        </Typography>
        <Tooltip title={allBusinessNames} arrow>
          <Chip
            label={`+${remainingCount}`}
            size="small"
            onClick={(e) => handleOpenUnitsPopover(e, businessesList)}
            sx={{
              height: 20,
              fontSize: '0.75rem',
              bgcolor: 'primary.light',
              color: 'primary.contrastText',
              cursor: 'pointer',
              '&:hover': {
                bgcolor: 'primary.main',
              },
            }}
          />
        </Tooltip>
      </Box>
    );
  };

  const filteredCollaborators = collaborators.filter((collab) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      collab.name?.toLowerCase().includes(searchLower) ||
      collab.email?.toLowerCase().includes(searchLower)
    );
  });

  return (
    <Container maxWidth="xl">
      <Box sx={{ mt: 4, mb: 4 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ManageAccountsIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
            <Typography variant="h5" component="h1">
              {t('userManagement.title')}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleInviteUser}
            sx={{ textTransform: 'none' }}
          >
            {t('userManagement.inviteUser')}
          </Button>
        </Box>

        <Paper sx={{ width: '100%' }}>
          {/* Tabs */}
          <Tabs
            value={currentTab}
            onChange={handleTabChange}
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              px: 3,
            }}
          >
            <Tab label={t('userManagement.usersTab')} sx={{ textTransform: 'none' }} />
            <Tab label={t('userManagement.permissionsTab')} sx={{ textTransform: 'none' }} />
          </Tabs>

          {/* Tab Content */}
          <Box sx={{ p: 3 }}>
            {currentTab === 0 ? (
              <>
                {/* Search */}
                <Box sx={{ mb: 3 }}>
                  <TextField
                    placeholder={t('userManagement.searchPlaceholder')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    size="small"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      maxWidth: 320,
                      '& .MuiInputBase-input::placeholder': {
                        fontSize: '0.875rem',
                        opacity: 0.7,
                      }
                    }}
                  />
                </Box>

                {/* Table */}
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>{t('userManagement.name')}</TableCell>
                        <TableCell>{t('userManagement.role')}</TableCell>
                        <TableCell>{t('userManagement.units')}</TableCell>
                        <TableCell align="center">{t('userManagement.status')}</TableCell>
                        <TableCell align="center">{t('userManagement.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {loading ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              {t('common.loading')}...
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : filteredCollaborators.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography color="text.secondary">
                              {t('userManagement.noUsers')}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        filteredCollaborators.map((collaborator, index) => (
                          <TableRow
                            key={collaborator.uuid}
                            hover
                            sx={{
                              '& td': {
                                borderBottom: index === filteredCollaborators.length - 1 ? 'none' : '1px solid',
                                borderColor: 'divider',
                              }
                            }}
                          >
                            <TableCell>
                              <Box>
                                <Typography variant="body2" fontWeight={500}>
                                  {collaborator.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {collaborator.email}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={getRoleLabel(collaborator)}
                                size="small"
                                sx={{
                                  bgcolor: '#E3F2FD',
                                  color: '#1976D2',
                                  fontWeight: 500,
                                }}
                              />
                            </TableCell>
                            <TableCell>
                              {renderBusinesses(collaborator)}
                            </TableCell>
                            <TableCell align="center">
                              <Switch
                                checked={collaborator.status === 'active'}
                                onChange={() => handleToggleStatus(collaborator.uuid, collaborator.status)}
                                color="primary"
                                disabled
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => handleEdit(collaborator)}
                                sx={{
                                  textTransform: 'none',
                                  color: 'text.primary',
                                  borderColor: 'divider',
                                  fontWeight: 400,
                                  px: 2,
                                  '&:hover': {
                                    borderColor: 'text.secondary',
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                  }
                                }}
                              >
                                {t('userManagement.edit')}
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            ) : (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <Typography color="text.secondary">
                  {t('userManagement.permissionsComingSoon')}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>

        {/* Invite User Modal */}
        <Dialog
          open={inviteModalOpen}
          onClose={handleCloseInviteModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t('userManagement.inviteTitle')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              <TextField
                fullWidth
                label={t('userManagement.emailField')}
                type="email"
                value={inviteForm.email}
                onChange={(e) => handleInviteFormChange('email', e.target.value)}
              />
              <TextField
                fullWidth
                label={t('userManagement.nameField')}
                value={inviteForm.name}
                onChange={(e) => handleInviteFormChange('name', e.target.value)}
              />
              <FormControl fullWidth>
                <InputLabel>{t('userManagement.roleField')}</InputLabel>
                <Select
                  value={inviteForm.role_uuid}
                  label={t('userManagement.roleField')}
                  onChange={(e) => handleInviteFormChange('role_uuid', e.target.value)}
                >
                  {roles
                    .filter(role => !role.is_owner)
                    .map((role) => (
                      <MenuItem key={role.uuid} value={role.uuid}>
                        {role.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              <Box>
                <Typography variant="body2" fontWeight={500} gutterBottom>
                  {t('userManagement.unitsField')}
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={inviteForm.allBusinesses}
                        onChange={(e) => handleInviteAllBusinessesToggle(e.target.checked)}
                      />
                    }
                    label={t('userManagement.allUnits')}
                  />

                  {!inviteForm.allBusinesses && businesses.map((business) => (
                    <FormControlLabel
                      key={business.uuid}
                      control={
                        <Checkbox
                          checked={inviteForm.selectedBusinesses.includes(business.uuid)}
                          onChange={() => handleInviteBusinessToggle(business.uuid)}
                        />
                      }
                      label={business.name}
                      sx={{ ml: 2 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseInviteModal} sx={{ textTransform: 'none' }}>
              {t('userManagement.cancel')}
            </Button>
            <Button
              variant="contained"
              onClick={handleInvite}
              sx={{ textTransform: 'none' }}
            >
              {t('userManagement.invite')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit User Modal */}
        <Dialog
          open={editModalOpen}
          onClose={handleCloseEditModal}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {t('userManagement.editTitle')}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 3 }}>
              <TextField
                fullWidth
                label={t('userManagement.emailField')}
                value={editForm.email}
                type="email"
                InputProps={{
                  readOnly: true,
                }}
                sx={{
                  '& .MuiInputBase-input': {
                    cursor: 'default',
                  }
                }}
              />

              <TextField
                fullWidth
                label={t('userManagement.nameField')}
                value={editForm.name}
                onChange={(e) => handleEditFormChange('name', e.target.value)}
              />

              <FormControl fullWidth>
                <InputLabel>{t('userManagement.roleField')}</InputLabel>
                <Select
                  value={editForm.role_uuid}
                  label={t('userManagement.roleField')}
                  onChange={(e) => handleEditFormChange('role_uuid', e.target.value)}
                >
                  {roles
                    .filter(role => !role.is_owner)
                    .map((role) => (
                      <MenuItem key={role.uuid} value={role.uuid}>
                        {role.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  {t('userManagement.unitsField')}
                </Typography>
                <FormGroup>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={editForm.allBusinesses}
                        onChange={(e) => handleAllBusinessesToggle(e.target.checked)}
                      />
                    }
                    label={t('userManagement.allUnits')}
                  />

                  {!editForm.allBusinesses && businesses.map((business) => (
                    <FormControlLabel
                      key={business.uuid}
                      control={
                        <Checkbox
                          checked={editForm.selectedBusinesses.includes(business.uuid)}
                          onChange={() => handleBusinessToggle(business.uuid)}
                        />
                      }
                      label={business.name}
                      sx={{ ml: 2 }}
                    />
                  ))}
                </FormGroup>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleCloseEditModal} sx={{ textTransform: 'none' }}>
              {t('userManagement.cancel')}
            </Button>
            <Button
              onClick={handleSaveEdit}
              variant="contained"
              sx={{ textTransform: 'none' }}
            >
              {t('userManagement.saveChanges')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Units Popover */}
        <Popover
          open={Boolean(unitsPopover.anchorEl)}
          anchorEl={unitsPopover.anchorEl}
          onClose={handleCloseUnitsPopover}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
        >
          <List sx={{ py: 1, minWidth: 200, maxWidth: 300 }}>
            {unitsPopover.businesses.map((business, index) => (
              <ListItem key={business.uuid || index} dense>
                <ListItemText
                  primary={business.name}
                  primaryTypographyProps={{
                    variant: 'body2',
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Popover>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default UserManagement;
